import { create } from "zustand";

import { t } from "../../i18n/i18n";

/** Accès au menu « Ajouter une annotation ici » (demande d'Alexis du 16/10/2026) : le clic droit sur la carte, le
 *  bouton « Annotation » de la barre du bas et l'appui long au doigt sur un endroit vide suivent les mêmes gardes
 *  (`annotationMenuBlock`) ; une bulle d'astuce les signale une seule fois par poste (`useAnnotationTip`). */

/** État de la carte lu par les gardes du menu. */
export interface AnnotationMenuContext {
  /** Carte du dossier révélée (dossier créé) et dispositif chargé. */
  revealed: boolean;
  hasDeployment: boolean;
  /** Mode édition du fuseau, tracé d'un terrain libre, tracé d'un graphique ou d'une annotation, « Orienter la
   *  manœuvre ». */
  editing: boolean;
  zoneDrawing: boolean;
  draft: boolean;
  orienting: boolean;
}

/** Ce qui empêche d'ouvrir le menu : `hidden` (pas de carte de dossier : le bouton n'est pas affiché), une opération
 *  en cours sur la carte (le bouton reste affiché, bloqué, et dit laquelle terminer), ou `null` (menu permis). Les
 *  autres gardes — dossier enregistré, lecture seule, emprise du camp ou du terrain — s'appliquent ensuite, comme au
 *  clic droit : à la pose (enregistrement désigné, bandeau de lecture seule) et au point visé (« Hors du camp »). */
export type AnnotationMenuBlock = "hidden" | "draft" | "orienting" | "editing" | "zoneDrawing" | null;

export function annotationMenuBlock(c: AnnotationMenuContext): AnnotationMenuBlock {
  if (c.draft) return "draft";
  if (c.orienting) return "orienting";
  if (!c.revealed || !c.hasDeployment) return "hidden";
  if (c.editing) return "editing";
  if (c.zoneDrawing) return "zoneDrawing";
  return null;
}

/** Avis d'un bouton bloqué et cible désignée (l'action attendue clignote). */
export function annotationBlockNotice(block: Exclude<AnnotationMenuBlock, "hidden" | null>): {
  text: string;
  detail: string;
  target: string;
} {
  switch (block) {
    case "draft":
      return {
        text: t("Terminez le tracé en cours pour ajouter une annotation"),
        detail: t("Le bandeau du tracé le termine ou l'abandonne."),
        target: '[data-attention="draft-banner"]',
      };
    case "zoneDrawing":
      return {
        text: t("Terminez le tracé du terrain pour ajouter une annotation"),
        detail: t("Le bandeau du tracé le termine ou l'abandonne."),
        target: '[data-attention="draft-banner"]',
      };
    case "orienting":
      return {
        text: t("Appliquez ou annulez l'orientation de la manœuvre pour ajouter une annotation"),
        detail: t("Les annotations se posent une fois la manœuvre orientée."),
        target: '[data-attention="orientation-apply"]',
      };
    case "editing":
      return {
        text: t("Quittez l'édition du fuseau pour ajouter une annotation"),
        detail: t("« Terminé », dans le bandeau du fuseau, quitte l'édition."),
        target: '[data-attention="manoeuvre-done"]',
      };
  }
}

/** Infobulle du bouton « Annotation » selon son état. */
export function annotationButtonTitle(block: AnnotationMenuBlock): string {
  return block === null || block === "hidden" ? t("Ajouter une annotation") : annotationBlockNotice(block).text;
}

// --- Bulle d'astuce ------------------------------------------------------------------------------------------------

/** Marque du poste : l'astuce a été montrée (commune au navigateur, pas propre à une licence). */
export const ANNOTATION_TIP_KEY = "generex:astuce-annotations";

/** Stockage du navigateur, injectable pour les essais. */
type TipStorage = Pick<Storage, "getItem" | "setItem">;

function storage(): TipStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** L'astuce a-t-elle déjà été montrée sur ce poste ? Stockage indisponible : non (elle paraît une fois par séance). */
export function annotationTipSeen(store: TipStorage | null = storage()): boolean {
  try {
    return store?.getItem(ANNOTATION_TIP_KEY) === "1";
  } catch {
    return false;
  }
}

export function markAnnotationTipSeen(store: TipStorage | null = storage()): void {
  try {
    store?.setItem(ANNOTATION_TIP_KEY, "1");
  } catch {
    // Stockage indisponible (navigation privée, données du site bloquées) : la séance s'en souvient seule.
  }
}

/** Bulle d'astuce : `idle` (pas encore proposée dans cette séance), `visible` (montrée, jusqu'à sa fermeture ou à
 *  l'ouverture du menu), `done` (fermée, ou déjà vue sur ce poste). Marquée vue dès qu'elle paraît : un rechargement
 *  ne la ramène pas. */
export const useAnnotationTip = create<{
  state: "idle" | "visible" | "done";
  /** Carte du dossier affichée, menu permis : la bulle paraît si elle n'a jamais été montrée sur ce poste. */
  offer: () => void;
  dismiss: () => void;
}>()((set, get) => ({
  state: "idle",
  offer: () => {
    if (get().state !== "idle") return;
    if (annotationTipSeen()) {
      set({ state: "done" });
      return;
    }
    markAnnotationTipSeen();
    set({ state: "visible" });
  },
  dismiss: () => {
    if (get().state === "done") return;
    markAnnotationTipSeen();
    set({ state: "done" });
  },
}));

/** Vues plein cadre posées sur la carte (`FULL_VIEWS` d'app/App.tsx, vérifié par l'essai) : la carte y est cachée,
 *  la bulle attend son retour. */
export const MAP_COVERING_VIEWS: ReadonlySet<string> = new Set([
  "country",
  "briefing",
  "orbats",
  "enemies",
  "received",
  "declination",
  "retex",
  "evaluation",
  "timeline",
  "coa",
  "order",
  "oplan",
]);

/** Délai avant l'apparition de la bulle : la carte du dossier a fini d'apparaître. */
export const ANNOTATION_TIP_DELAY_MS = 1200;
